import { initMessages, msg } from "./messages";
import { ensureAuthenticated, setAuthRecoveryHandler } from "./auth";
import {
    getActiveHouseholdId,
    householdsApi,
    initHouseholds,
    refreshMemberships,
    setActiveHousehold,
} from "./households";
import { showToast } from "./components/toast";
import { beginNavigation, isNavigationStale } from "./navigation";
import { startAutoSync, onSyncStateChange, performSync } from "./sync/syncEngine";
import type { SyncState } from "./sync/syncEngine";
import { renderDashboard } from "./views/dashboard";
import { renderGrocery } from "./views/grocery";
import { renderPantry } from "./views/pantry";
import { renderLocations } from "./views/locations";
import { renderHouseholds } from "./views/households";

const mainEl = document.getElementById("app-main")!;
const navLinks = document.querySelectorAll(".nav-link");

type RouteRender = (container: HTMLElement, gen: number) => Promise<void>;

const routes: Record<string, { title: string; render: RouteRender }> = {
    "/": { title: "Home", render: renderDashboard },
    "/pantry": { title: "Pantry", render: renderPantry },
    "/grocery": { title: "Grocery", render: renderGrocery },
    "/zones": { title: "Zones", render: renderLocations },
    "/households": { title: "Households", render: renderHouseholds },
};

let currentRoute = "";
let currentHash = "";
let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function getRoute(): string {
    const hash = location.hash.slice(1) || "/";
    let path = hash.split("?")[0];
    if (path === "/locations") path = "/zones";
    return routes[path] ? path : "/";
}

function setActiveNav(route: string): void {
    navLinks.forEach((link) => {
        link.classList.toggle("active", (link as HTMLElement).dataset.route === route);
    });
}

async function navigate(): Promise<void> {
    const hash = location.hash || "#/";
    const route = getRoute();
    if (hash === currentHash && route !== "/") return;

    const gen = beginNavigation();

    currentRoute = route;
    currentHash = hash;
    const config = routes[route];
    document.title = config.title === "Home" ? "Pantry Pal" : `${config.title} · Pantry Pal`;
    setActiveNav(route);
    await config.render(mainEl, gen);

    if (isNavigationStale(gen)) return;
}

function initRouter(): void {
    window.addEventListener("hashchange", navigate);

    navigate();
}

function initSyncStatus(): void {
    const status = document.getElementById("sync-status")!;
    const icon = status.querySelector<HTMLElement>(".sync-status__icon")!;

    const SHOW_DELAY_MS = 300;
    const MIN_VISIBLE_MS = 600;
    let showTimer: number | undefined;
    let hideTimer: number | undefined;
    let visibleSince = 0;

    function setIcon(state: SyncState): void {
        status.classList.remove("sync-status--syncing", "sync-status--error");
        if (state === "syncing") {
            status.classList.add("sync-status--syncing");
            icon.textContent = "sync";
        } else if (state === "error") {
            status.classList.add("sync-status--error");
            icon.textContent = "sync_problem";
        } else if (state === "offline") {
            icon.textContent = "cloud_off";
        }
    }

    function showIcon(state: SyncState): void {
        window.clearTimeout(showTimer);
        setIcon(state);
        status.classList.remove("hidden");
        visibleSince = Date.now();
    }

    function hideIcon(): void {
        const elapsed = Date.now() - visibleSince;
        const remaining = Math.max(0, MIN_VISIBLE_MS - elapsed);
        window.clearTimeout(hideTimer);
        hideTimer = window.setTimeout(() => {
            status.classList.add("hidden");
        }, remaining);
    }

    onSyncStateChange((state) => {
        window.clearTimeout(showTimer);
        if (state === "syncing") {
            showTimer = window.setTimeout(() => showIcon("syncing"), SHOW_DELAY_MS);
        } else if (state === "error") {
            showIcon("error");
        } else if (state === "offline") {
            showIcon("offline");
        } else {
            hideIcon();
        }
    });
}

function initServiceWorker(): void {
    if (!("serviceWorker" in navigator)) return;

    void (async () => {
        try {
            const res = await fetch("/api/health", { cache: "no-store", credentials: "include" });
            const { dev } = (await res.json()) as { dev?: boolean };
            if (dev) {
                const regs = await navigator.serviceWorker.getRegistrations();
                await Promise.all(regs.map((r) => r.unregister()));
                // continue to register the dev service worker below
            }
        } catch {
            /* fall through to normal registration */
        }

        try {
            const reg = await navigator.serviceWorker.register("/sw.js");
            await subscribeToPush(reg);
        } catch (err) {
            console.warn("Service worker registration failed:", err);
        }

        navigator.serviceWorker.addEventListener("message", (event) => {
            if (event.data?.type === "SYNC_NOW") {
                void performSync();
            }
        });
    })();
}

function isIOS(): boolean {
    return (
        /iPhone|iPad|iPod/.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
    );
}

async function subscribeToPush(reg: ServiceWorkerRegistration): Promise<void> {
    if (!("PushManager" in window) || !("Notification" in window)) return;

    if (Notification.permission === "default" && isIOS()) {
        // iOS requires requestPermission() to be called from a user gesture —
        // show an in-app prompt instead of asking directly.
        const banner = document.getElementById("notification-banner")!;
        const enableBtn = document.getElementById("notification-enable-btn") as HTMLButtonElement;
        const dismissBtn = document.getElementById("notification-dismiss") as HTMLButtonElement;
        if (localStorage.getItem("notification-prompt-dismissed")) return;
        banner.classList.remove("hidden");
        dismissBtn.addEventListener("click", () => {
            banner.classList.add("hidden");
            localStorage.setItem("notification-prompt-dismissed", "1");
        });
        enableBtn.addEventListener(
            "click",
            () => {
                banner.classList.add("hidden");
                localStorage.setItem("notification-prompt-dismissed", "1");
                void subscribeToPushNow(reg);
            },
            { once: true },
        );
        return;
    }

    await subscribeToPushNow(reg);
}

async function subscribeToPushNow(reg: ServiceWorkerRegistration): Promise<void> {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return;

    try {
        const res = await fetch("/api/notifications/vapid-public-key", { credentials: "include" });
        if (!res.ok) return;
        const { publicKey } = (await res.json()) as { publicKey?: string };
        if (!publicKey) return;

        const sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey),
        });

        const subJson = JSON.parse(JSON.stringify(sub)) as {
            endpoint: string;
            keys: { p256dh: string; auth: string };
        };
        await fetch("/api/notifications/subscribe", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(subJson),
            credentials: "include",
        });
    } catch (err) {
        console.warn("Push subscription failed:", err);
    }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = window.atob(base64);
    const buffer = new ArrayBuffer(rawData.length);
    const outputArray = new Uint8Array(buffer);
    for (let i = 0; i < rawData.length; i++) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray as unknown as Uint8Array<ArrayBuffer>;
}

function initInstallPrompt(): void {
    const banner = document.getElementById("install-banner")!;
    const installBtn = document.getElementById("install-btn") as HTMLButtonElement;
    const dismissBtn = document.getElementById("install-dismiss") as HTMLButtonElement;
    const bannerText = banner.querySelector(".install-banner-text")!;
    bannerText.textContent = msg("app/install-banner");

    const isStandalone =
        window.matchMedia("(display-mode: standalone)").matches ||
        (navigator as Navigator & { standalone?: boolean }).standalone;

    if (isStandalone) return;

    window.addEventListener("beforeinstallprompt", (e) => {
        e.preventDefault();
        deferredInstallPrompt = e as BeforeInstallPromptEvent;
        banner.classList.remove("hidden");
    });

    installBtn.addEventListener("click", async () => {
        if (!deferredInstallPrompt) return;
        await deferredInstallPrompt.prompt();
        const { outcome } = await deferredInstallPrompt.userChoice;
        deferredInstallPrompt = null;
        banner.classList.add("hidden");
        if (outcome === "accepted") localStorage.setItem("install-hint-dismissed", "1");
    });

    dismissBtn.addEventListener("click", () => {
        banner.classList.add("hidden");
        localStorage.setItem("install-hint-dismissed", "1");
    });

    if (localStorage.getItem("install-hint-dismissed")) return;

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (!isMobile) return;

    setTimeout(() => {
        if (deferredInstallPrompt || isStandalone) return;
        banner.classList.remove("hidden");
        bannerText.textContent = msg("app/install-hint");
        installBtn.classList.add("hidden");
    }, 3000);
}

const PENDING_INVITE_KEY = "pantry-pending-invite";

function stashInviteLink(): void {
    const params = new URLSearchParams(location.search);
    const code = params.get("join");
    if (!code) return;
    localStorage.setItem(PENDING_INVITE_KEY, code);
    params.delete("join");
    const url = params.size ? `${location.pathname}?${params}` : location.pathname;
    history.replaceState(null, "", url);
}

/** Join a household from a shared ?join= link once signed in. */
async function consumePendingInvite(): Promise<void> {
    const code = localStorage.getItem(PENDING_INVITE_KEY);
    if (!code) return;
    localStorage.removeItem(PENDING_INVITE_KEY);
    try {
        const joined = await householdsApi.join(code);
        await refreshMemberships();
        setActiveHousehold(joined.id);
        showToast(`Joined "${joined.name}".`);
    } catch {
        /* invalid/expired code — fall through to normal startup */
    }
}

async function bootstrap(): Promise<void> {
    stashInviteLink();
    await initMessages();
    await ensureAuthenticated();
    await consumePendingInvite();
    await initHouseholds();
    if (getActiveHouseholdId() === null) {
        location.hash = "#/households";
    }
    initRouter();
    setAuthRecoveryHandler(() => {
        void navigate();
    });
    initSyncStatus();
    initServiceWorker();
    initInstallPrompt();
    startAutoSync();
}

void bootstrap();
